import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import type { CostCenter } from '@src/models/interfaces/costCenter.interface';
import {
  expandCostCenterIds,
  normalizeCostCenterFilterIds,
} from '@src/packages/costCenters/utils/expandCostCenterIds';
import { REMOTE_CONFIG_KEYS } from '@src/packages/utils/hooks/remoteConfigs/remoteConfigKeys';
import { useRemoteConfigAccess } from '@src/packages/utils/hooks/remoteConfigs/useRemoteConfigAccess';
import CostCenterService from '@src/services/CostCenterService';

/**
 * Loads active cost centers (when the module is enabled) and expands the
 * selected filter ids so a client includes its branches for query payloads.
 *
 * When a selection is present, `queryIds` stays `undefined` until the list has
 * loaded so callers do not send an unexpanded first request.
 * When the module is off, expansion always returns `undefined` (no filter sent).
 */
export const useCostCenterFilterIds = (selectedIds: string[] | undefined) => {
  const { isAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.COST_CENTERS);
  const organizationId = useSelector(
    ({ organizations }: { organizations?: { organization?: { id?: string } } }) =>
      organizations?.organization?.id,
  );
  const serviceRef = useRef<CostCenterService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new CostCenterService();
  }
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const normalized = useMemo(
    () => (isAllowed ? normalizeCostCenterFilterIds(selectedIds) : []),
    [isAllowed, selectedIds],
  );

  useEffect(() => {
    if (!isAllowed || !organizationId) {
      setCostCenters([]);
      setListLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setListLoaded(false);
    serviceRef.current!
      .list(organizationId, true)
      .then(list => {
        if (cancelled) return;
        setCostCenters(list);
        setListLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCostCenters([]);
        setListLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAllowed, organizationId]);

  const isExpandReady = !isAllowed || !normalized.length || listLoaded;

  const expandSelection = useCallback(
    (ids: string[] | undefined): string[] | undefined => {
      if (!isAllowed) return undefined;
      const next = normalizeCostCenterFilterIds(ids);
      if (!next.length) return undefined;
      if (!listLoaded) return undefined;
      return expandCostCenterIds(next, costCenters);
    },
    [costCenters, isAllowed, listLoaded],
  );

  const queryIds = useMemo(
    () => expandSelection(normalized),
    [expandSelection, normalized],
  );

  return {
    isAllowed,
    selectedIds: normalized,
    /** Expanded ids for API / PDF payloads; undefined when no filter, RC off, or list not ready. */
    queryIds,
    /** False while a non-empty selection waits for the cost-center list (RC on). */
    isExpandReady,
    /**
     * True once `CostCenterService.list` has settled (or RC is off / no org).
     * Use this to gate fetches that expand an override selection before commit.
     */
    listLoaded,
    /**
     * Expand any raw selection with the currently loaded tree.
     * Returns `undefined` when RC is off, selection is empty, or the list is not ready.
     */
    expandSelection,
    costCenters,
  };
};
